import type { InputProps as AntdInputProps, InputRef } from 'antd'
import { Input as AntdInput, Form, message } from 'antd'

import { forwardRef, useImperativeHandle, useRef, useState, type ChangeEvent } from 'react'

import { Typography } from '@/components/Typography'

import styles from './styles.module.scss'

interface TagItemProps {
  item: string
  onDelete: (tag: string) => void
}

function TagItem({ item, onDelete }: TagItemProps) {
  return (
    <div title={item} className={styles.tagItemContainer}>
      <span>{item.length > 20 ? `${item.slice(0, 20)}...` : item}</span>
      <button onClick={() => onDelete(item)} className={styles.tagItemDeleteButton}>
        <svg
          width="10"
          height="10"
          viewBox="64 64 896 896"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z"
            fill="black"
            fillOpacity="0.45"
          />
        </svg>
      </button>
    </div>
  )
}

interface TagInputRef {
  focus: () => void
  blur: () => void
}

interface TagInputProps extends Omit<AntdInputProps, 'onChange'> {
  className?: string
  style?: React.CSSProperties
  placeholder?: string
  value?: string[]
  onChange?: (value: string[]) => void
  description?: string
}

function isEnterKey(e: React.KeyboardEvent<HTMLInputElement>) {
  return e.code === 'Enter' || e.code === 'NumpadEnter'
}

export const TagInput = forwardRef<TagInputRef, TagInputProps>(
  ({ value = [], onChange, placeholder, style, description, className, ...props }, ref) => {
    const [content, setContent] = useState<string>()
    const inputRef = useRef<InputRef>(null)

    const { status: validationStatus } = Form.Item.useStatus()

    const prefixCls = 'ant-input'
    const statusCls = `${prefixCls}-status-${validationStatus}`

    useImperativeHandle(ref, () => ({
      blur: () => {
        inputRef.current?.blur()
      },
      focus: () => {
        inputRef.current?.focus()
      },
    }))

    function handleDelete(tag: string) {
      onChange?.(value.filter((item) => item !== tag))
    }

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
      setContent(e.target.value)
    }

    function handleKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
      if (isEnterKey(e) && content) {
        if (value.includes(content)) {
          message.warn(`Content: "${content}" already exists`)
          return
        }

        onChange?.([...value, content])

        setContent('')
      }
    }

    function handleBlur() {
      if (content) {
        if (value.includes(content)) {
          message.warn(`Content: "${content}" already exists`)
          return
        }

        onChange?.([...value, content])

        setContent('')
      }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (!isEnterKey(e)) return

      e.preventDefault()
    }

    const classNames = [prefixCls, statusCls, styles.inputContainer, className].join(' ')

    return (
      <div className={styles.wrapper}>
        <div
          className={classNames}
          onClick={() => inputRef.current?.focus()}
          style={{
            ...(value.length !== 0 && {
              paddingLeft: '.25rem',
              paddingTop: '.1875rem',
              paddingBottom: '.1875rem',
            }),
            ...style,
          }}
        >
          {value.map((item) => (
            <TagItem key={item} item={item} onDelete={handleDelete} />
          ))}

          <AntdInput
            ref={inputRef}
            bordered={false}
            placeholder={!value.length ? placeholder : ''}
            value={content}
            className={styles.input}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            {...props}
          />
        </div>
        {description && validationStatus !== 'error' && (
          <Typography.Text className={styles.description}>{description}</Typography.Text>
        )}
      </div>
    )
  },
)

TagInput.displayName = 'TagInput'
